/* Push subscription helper — registers the service worker (if not already),
 * subscribes via VAPID public key from the server, and posts the subscription
 * to the backend. Callers ask about permission first via `pushSupported()`. */
import { api } from "@/lib/api";

const SW_URL = "/service-worker.js";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const b64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function pushSupported() {
  return typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;
}

export async function pushPermissionState() {
  if (!pushSupported()) return "unsupported";
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

async function getRegistration() {
  const existing = await navigator.serviceWorker.getRegistration();
  if (existing) return existing;
  return navigator.serviceWorker.register(SW_URL);
}

export async function currentSubscription() {
  if (!pushSupported()) return null;
  const reg = await getRegistration();
  const sub = await reg.pushManager.getSubscription();
  return sub;
}

export async function enablePush() {
  if (!pushSupported()) throw new Error("Push notifications aren't supported in this browser.");
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Notification permission was declined.");
  const reg = await getRegistration();
  const existing = await reg.pushManager.getSubscription();
  if (existing) {
    await api.post("/push/subscribe", existing.toJSON());
    return existing.toJSON();
  }
  const { data } = await api.get("/push/vapid-public-key");
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(data.key),
  });
  await api.post("/push/subscribe", sub.toJSON());
  return sub.toJSON();
}

export async function disablePush() {
  if (!pushSupported()) return { ok: true };
  const reg = await getRegistration();
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { ok: true };
  await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
  await sub.unsubscribe();
  return { ok: true };
}

export async function sendTestPush() {
  return api.post("/push/test", {
    title: "Prestigious test",
    body: "If you saw this you're all set.",
    url: "/",
  });
}
